// SPDX-License-Identifier: LicenseRef-Proprietary

/**
 * Auto-Initialize Tree Hook
 * 
 * Purpose: Automatically initialize decision tree when vaccine dashboard is configured
 * Features: Checks if tree is empty, generates "Vaccinate vs No Vaccinate" structure
 * 
 * @note Only runs once when vaccine inputs are valid
 * @note Uses template generator from lib/vaccineTreeTemplate
 */

import { useEffect, useRef } from 'react';
import { useVaccineClientStore } from '../store/vaccineClientStore';
import { useVaccineDashboardStore } from '../store/vaccineDashboardStore';
import { initializeVaccineTree } from '../lib/vaccineTreeTemplate';

/**
 * Hook to auto-initialize decision tree from vaccine dashboard inputs
 * 
 * This hook monitors the vaccine dashboard state and automatically generates
 * a "Vaccinate vs No Vaccinate" decision tree when:
 * 1. The tree is empty (no nodes)
 * 2. Vaccine inputs are valid (cost, efficacy, etc.)
 * 3. Initialization hasn't already been attempted
 * 
 * @example
 * ```typescript
 * // In App.tsx or DecisionTreeBuilder.tsx
 * function VaccineClientApp() {
 *   useAutoInitializeTree(); // Auto-generate tree on mount
 *   
 *   return (
 *     <div>
 *       <VaccineDashboard />
 *       <DecisionTreeBuilder />
 *     </div>
 *   );
 * }
 * ```
 */
export function useAutoInitializeTree() {
  // Track if initialization has been attempted to prevent infinite loops
  const initAttemptedRef = useRef(false);
  
  useEffect(() => {
    // Only run once on mount
    if (initAttemptedRef.current) {
      return;
    }
    
    // Skip during import hydration to prevent interference
    const isImporting =
      useVaccineDashboardStore.getState().isImporting ||
      useVaccineClientStore.getState().isImporting;
    
    if (isImporting) {
      return;
    }
    
    // Mark as attempted immediately
    initAttemptedRef.current = true;
    
    // Check if tree already has nodes
    const currentNodes = useVaccineClientStore.getState().nodes;
    const hasNodes = Object.keys(currentNodes).length > 0;
    
    if (hasNodes) {
      return;
    }
    
    // Get vaccine inputs
    const vaccineInputs = useVaccineDashboardStore.getState().inputs;
    
    // Check if vaccine inputs are valid
    if (!vaccineInputs.vaccineCostPerDoseEUR || vaccineInputs.vaccineCostPerDoseEUR <= 0) {
      return;
    }
    
    if (!vaccineInputs.treatmentCostPerCaseEUR || vaccineInputs.treatmentCostPerCaseEUR <= 0) {
      return;
    }
    
    if (!vaccineInputs.vaccineEfficacyPct || vaccineInputs.vaccineEfficacyPct <= 0 || vaccineInputs.vaccineEfficacyPct > 100) {
      return;
    }
    
    try {
      // Initialize tree with vaccine dashboard inputs
      initializeVaccineTree(vaccineInputs);
    } catch (error) {
      console.error('[useAutoInitializeTree] Failed to initialize tree:', error);
    }
  }, []); // Empty dependency array - only run once on mount
}

/**
 * Hook to manually trigger tree reset and re-initialization
 * 
 * @returns Function to reset and reinitialize the tree
 * 
 * @example
 * ```typescript
 * function ResetButton() {
 *   const resetTree = useTreeReset();
 *   
 *   return (
 *     <button onClick={resetTree}>
 *       Reset to Default Tree
 *     </button>
 *   );
 * }
 * ```
 */
export function useTreeReset() {
  const { clearTree } = useVaccineClientStore();
  const vaccineInputs = useVaccineDashboardStore((state) => state.inputs);
  
  return () => {
    // Clear existing tree
    clearTree();
    
    // Reinitialize with current vaccine inputs
    if (vaccineInputs.vaccineCostPerDoseEUR > 0 && 
        vaccineInputs.treatmentCostPerCaseEUR > 0 &&
        vaccineInputs.vaccineEfficacyPct > 0 && 
        vaccineInputs.vaccineEfficacyPct <= 100) {
      initializeVaccineTree(vaccineInputs);
    }
  };
}
